import React, { useMemo } from 'react';

import { Card, Progress, Typography } from 'antd';

import type { ChartData } from '@/shared/api/openmetadata';

import styles from './PlatformInsights.module.css';

const { Title, Text } = Typography;

const INSIGHT_COLORS = {
  DESCRIPTION: '#52c41a',
  PII: '#1890ff',
  TIER: '#722ed1',
  OWNER: '#fa8c16',
} as const;

type PlatformInsightsProps = {
  assetsWithDescription?: ChartData;
  assetsWithPii?: ChartData;
  assetsWithTier?: ChartData;
  assetsWithOwners?: ChartData;
};

type InsightCardData = {
  title: string;
  percentage: number;
  change: number;
  color: string;
};

const calculatePercentage = (chartData?: ChartData): number => {
  if (!chartData || !chartData.results || chartData.results.length === 0) {
    return 0;
  }
  const latestCount = chartData.results[chartData.results.length - 1]?.count || 0;
  return Math.round(latestCount * 10) / 10;
};

const calculateChange = (chartData?: ChartData): number => {
  if (!chartData || !chartData.results || chartData.results.length < 2) {
    return 0;
  }

  const firstCount = chartData.results[0]?.count || 0;
  const latestCount = chartData.results[chartData.results.length - 1]?.count || 0;

  return Math.round((latestCount - firstCount) * 10) / 10;
};

export const PlatformInsights: React.FC<PlatformInsightsProps> = ({
  assetsWithDescription,
  assetsWithPii,
  assetsWithTier,
  assetsWithOwners,
}) => {
  const insights = useMemo<InsightCardData[]>(
    () => [
      {
        title: '설명 커버리지',
        percentage: calculatePercentage(assetsWithDescription),
        change: calculateChange(assetsWithDescription),
        color: INSIGHT_COLORS.DESCRIPTION,
      },
      {
        title: 'PII 커버리지',
        percentage: calculatePercentage(assetsWithPii),
        change: calculateChange(assetsWithPii),
        color: INSIGHT_COLORS.PII,
      },
      {
        title: '계층 커버리지',
        percentage: calculatePercentage(assetsWithTier),
        change: calculateChange(assetsWithTier),
        color: INSIGHT_COLORS.TIER,
      },
      {
        title: '소유자 커버리지',
        percentage: calculatePercentage(assetsWithOwners),
        change: calculateChange(assetsWithOwners),
        color: INSIGHT_COLORS.OWNER,
      },
    ],
    [assetsWithDescription, assetsWithPii, assetsWithTier, assetsWithOwners]
  );

  return (
    <div className={styles.platformInsights}>
      <Title level={4} className={styles.sectionTitle}>
        플랫폼 인사이트
      </Title>
      <div className={styles.insightsGrid}>
        {insights.map((insight) => (
          <Card key={insight.title} className={styles.insightCard}>
            <div className={styles.cardHeader}>
              <Text className={styles.cardTitle}>{insight.title}</Text>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.percentageWrapper}>
                <Text className={styles.percentage}>{insight.percentage}%</Text>
                <div className={styles.changeRate}>
                  <Text className={insight.change >= 0 ? styles.positive : styles.negative}>
                    {insight.change > 0 ? '+' : ''}
                    {insight.change}%
                  </Text>
                  <Text className={styles.changeLabel}>최근 7일간</Text>
                </div>
              </div>
              <Progress
                percent={insight.percentage}
                strokeColor={insight.color}
                showInfo={false}
                className={styles.progress}
              />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
